/**
 * CAD paper-space layout commands (docs/plans/2d/2d-full-parity-plan.md
 * Faz 9, option A): the 2D workspace's LAYOUT / MVIEW / VPLAYER / VPCLIP
 * / VPSYNC / CHSPACE / TILEMODE commands run against the dedicated
 * useLayoutStore (paper sheets with floating viewports over the model),
 * instead of the BIM sheet bridge used by the BIM workspace. Registered
 * after sheetCommands so the CAD ids resolve here first (last spread wins).
 */

import { runInteraction, PT, PTxt, PN, PLoop, rubberBand } from './lib';
import type { RunnerMap } from '../runnerRegistry';
import { useLayoutStore } from '../../../state/useLayoutStore';
import { notify } from '../../../utils/notify';
import i18n from '../../../i18n';

export const layoutRunners: RunnerMap = {
    // LAYOUT: NEW <name> / list / <name> (switch) / DEL <name> / RENAME.
    'file.layout': (args) => {
        const store = useLayoutStore.getState();
        const sub = (args?.[0] ?? 'list').toUpperCase();
        if (sub === 'NEW') {
            const name = args?.[1]?.trim() || undefined;
            const layout = store.addLayout(name);
            store.setActive(layout.id);
            return `LAYOUT: "${layout.name}" created and current (${layout.paper} paper).`;
        }
        if (sub === 'DEL') {
            const name = args?.[1]?.trim();
            const hit = store.layouts.find((l) => l.name === name);
            if (!hit) return 'LAYOUT DEL: specify an existing layout name.';
            store.removeLayout(hit.id);
            return `LAYOUT: "${hit.name}" deleted.`;
        }
        if (sub === 'RENAME') {
            const old = args?.[1]?.trim(), newName = args?.[2]?.trim();
            const hit = store.layouts.find((l) => l.name === old);
            if (!hit || !newName) return 'LAYOUT RENAME: <old> <new> required.';
            store.renameLayout(hit.id, newName);
            return `LAYOUT: renamed to "${newName}".`;
        }
        if (sub !== 'LIST') {
            // bare LAYOUT <name> switches
            const name = (args?.[0] ?? '').trim();
            const hit = store.layouts.find((l) => l.name.toLowerCase() === name.toLowerCase());
            if (hit) {
                store.setActive(hit.id);
                window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
                return `LAYOUT: switched to "${hit.name}" (paper space).`;
            }
            return `LAYOUT: no layout "${name}". ${store.layouts.length ? `Known: ${store.layouts.map((l) => l.name).join(', ')}` : 'Create one: LAYOUT NEW.'}`;
        }
        if (store.layouts.length === 0) return 'LAYOUT: no layouts — LAYOUT NEW to create.';
        return `LAYOUTS: ${store.layouts.map((l) => l.name).join(', ')} (active: ${store.activeId ? store.layouts.find((x) => x.id === store.activeId)?.name : 'Model'})`;
    },

    // MVIEW: place a floating viewport on the current layout by two corners.
    'file.mview': () => {
        const store = useLayoutStore.getState();
        if (!store.activeId) return 'MVIEW: switch to a layout first (LAYOUT <name>).';
        const layout = store.layouts.find((l) => l.id === store.activeId)!;
        runInteraction({
            steps: [
                PT('MVIEW: first corner of the viewport (paper)'),
                PT('MVIEW: opposite corner'),
                PN('MVIEW: viewport scale (paper per model unit) <0.02>'),
            ],
            preview: rubberBand('#8fd3ff'),
            complete: (pts, values) => {
                const [a, b] = pts;
                const w = Math.abs(b.x - a.x), h = Math.abs(b.y - a.y);
                if (w < 0.005 || h < 0.005) return 'MVIEW: viewport too small on paper.';
                const scale = Number(values[2]) > 0 ? Number(values[2]) : 0.02;
                const vp = store.addViewport(layout.id, {
                    cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2,
                    w, h,
                    viewCx: 0, viewCy: 0,
                    scale,
                    frozenLayers: [],
                    showTitle: true,
                    title: layout.name,
                });
                void vp;
                window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
                return `MVIEW: viewport placed at scale 1:${Math.round(1 / scale)}.`;
            },
        });
        return 'MVIEW: specify first corner';
    },

    // CHSPACE: toggle model <-> paper (the CAD layout model of paper space).
    'space.chspace': () => {
        const store = useLayoutStore.getState();
        if (store.activeId) {
            store.setActive(null);
            notify(i18n.t('tools.notify.chspaceBackToModelSpace'), 'info');
            return 'CHSPACE: model space active.';
        }
        if (store.layouts.length === 0) {
            const layout = store.addLayout('Layout1');
            store.setActive(layout.id);
            notify(i18n.t('tools.notify.chspaceCreatedLayout1AndSwitchedTo'), 'info');
            return 'CHSPACE: paper space active (Layout1).';
        }
        const next = store.layouts[0];
        store.setActive(next.id);
        window.dispatchEvent(new CustomEvent('yap:zoom-to-fit'));
        return `CHSPACE: paper space active (${next.name}).`;
    },

    // TILEMODE: 0 = model space, 1/anything = switch to current/first layout.
    'view.tilemode': (args) => {
        const store = useLayoutStore.getState();
        const v = Number(args?.[0] ?? 1);
        if (!v) {
            store.setActive(null);
            return 'TILEMODE: 0 — model space.';
        }
        const target = store.activeId ?? store.layouts[0]?.id;
        if (!target) return 'TILEMODE: 1 requested but no layouts — LAYOUT NEW first.';
        store.setActive(target);
        return 'TILEMODE: 1 — paper (layout) space.';
    },

    // VPLAYER: freeze/thaw a layer in every viewport of the active layout.
    'file.vpLayer': (args) => {
        const store = useLayoutStore.getState();
        if (!store.activeId) return 'VPLAYER: switch to a layout first.';
        const layout = store.layouts.find((l) => l.id === store.activeId)!;
        if (layout.viewports.length === 0) return 'VPLAYER: place a viewport first (MVIEW).';
        const apply = (layerName: string): string => {
            // Read the pre-toggle state to report the resulting word correctly.
            const wasFrozen = layout.viewports[0].frozenLayers.includes(layerName);
            layout.viewports.forEach((vp) => {
                store.updateViewport(layout.id, vp.id, {
                    frozenLayers: wasFrozen
                        ? vp.frozenLayers.filter((x) => x !== layerName)
                        : [...vp.frozenLayers, layerName],
                });
            });
            return `VPLAYER: layer "${layerName}" ${wasFrozen ? 'thawed' : 'frozen'} in ${layout.viewports.length} viewport(s).`;
        };
        const direct = (args ?? []).join(' ').trim();
        if (direct) return apply(direct);
        runInteraction({
            steps: [PTxt('VPLAYER: layer name to freeze/thaw in all viewports')],
            complete: (_p, values) => apply(String(values[0] ?? '').trim()),
        });
        return 'VPLAYER: enter layer name';
    },

    // VPCLIP: polygon boundary on the active layout's last viewport.
    'file.vpClip': () => {
        const store = useLayoutStore.getState();
        if (!store.activeId) return 'VPCLIP: switch to a layout first.';
        const layout = store.layouts.find((l) => l.id === store.activeId)!;
        const vp = layout.viewports[layout.viewports.length - 1];
        if (!vp) return 'VPCLIP: place a viewport first (MVIEW).';
        runInteraction({
            steps: [PLoop('VPCLIP: clip boundary points on paper (Enter to finish)', 3)],
            preview: rubberBand('#ff88ff'),
            complete: (pts) => {
                const clip: [number, number][] = pts.map((p) => [p.x, p.y]);
                store.updateViewport(layout.id, vp.id, { clip });
                return `VPCLIP: viewport clipped to a ${clip.length}-vertex boundary.`;
            },
        });
        return 'VPCLIP: specify boundary points';
    },

    // VPSYNC: make all viewports of the active layout share the first one's scale.
    'file.vpSync': () => {
        const store = useLayoutStore.getState();
        if (!store.activeId) return 'VPSYNC: switch to a layout first.';
        const layout = store.layouts.find((l) => l.id === store.activeId)!;
        if (layout.viewports.length < 2) return 'VPSYNC: need 2+ viewports to sync.';
        const base = layout.viewports[0];
        layout.viewports.slice(1).forEach((vp) => store.updateViewport(layout.id, vp.id, { scale: base.scale }));
        return `VPSYNC: ${layout.viewports.length - 1} viewport(s) matched to 1:${Math.round(1 / base.scale)}.`;
    },
};
